import { Badge, FormGroup, Label } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { Dayjs } from "dayjs";
import React from "react";
import { BaseScheduleRepeat, Schedule, WeekdayCopy } from "types";
import { getScheduleSummary } from "../../helpers/scheduleSummaryHelper";
import { getAllWeekdays } from "../../models/Weekday";
import { NoTranslate } from "./NoTranslate";
import { ScheduleTimeRanges } from "./ScheduleTimeRanges";

interface ScheduleSummaryProps {
    schedule: Schedule;
    showDetails?: boolean;
}

export const ScheduleSummary = ({ schedule, showDetails = true }: ScheduleSummaryProps) => {
    return (
        <div>
            <div className="mb-5 flex items-center justify-between">
                <span className="max-w-50 whitespace-normal text-md font-bold">
                    {schedule.name || (schedule ? getScheduleSummary(schedule) : "")}
                </span>
                <Badge color={schedule.type === "available" ? "success" : "danger"} size="medium">
                    <span className="capitalize">{schedule.type}</span>
                </Badge>
            </div>

            {schedule.repeat === BaseScheduleRepeat.WEEKLY ? (
                <DateRange start={schedule.start} end={schedule.end} />
            ) : schedule.repeat === BaseScheduleRepeat.CUSTOM ? (
                <CustomDates dates={schedule.dates} />
            ) : null}

            {schedule.repeat === BaseScheduleRepeat.WEEKLY && showDetails ? (
                <FormGroup>
                    <Label>Days of Week</Label>
                    <div className="flex space-x-2">
                        {getAllWeekdays().map((day) => (
                            <Badge
                                key={day}
                                color={schedule.days?.includes(day) ? "primary" : "secondary"}
                                data-testid="schedule-summary-days-of-week"
                            >
                                <span className={clsx(schedule.days?.includes(day) ? "text-black" : "text-gray-dark")}>
                                    {WeekdayCopy[day]}
                                </span>
                            </Badge>
                        ))}
                    </div>
                </FormGroup>
            ) : null}

            {showDetails && schedule.timeRanges?.length ? (
                <FormGroup>
                    <Label>Time Ranges</Label>
                    <ScheduleTimeRanges timeRanges={schedule.timeRanges} />
                </FormGroup>
            ) : null}
        </div>
    );
};

interface DateRangeProps {
    start: Dayjs | string | null;
    end: Dayjs | string | null;
    showSellerTimezone?: boolean;
}

export const DateRange = ({ start, end, showSellerTimezone = false }: DateRangeProps) => {
    const startDate = start ? formatDate(start, DateFormat.DATE_LONG, showSellerTimezone) : "No start";
    const endDate = end ? formatDate(end, DateFormat.DATE_LONG, showSellerTimezone) : "No end";

    return (
        <div className="flex">
            <FormGroup className="w-44">
                <Label>Start Date</Label>
                <NoTranslate as="div" data-testid="schedule-summary-start-date" className="text-gray-darker">
                    {startDate}
                </NoTranslate>
            </FormGroup>

            <FormGroup>
                <Label>End Date</Label>
                <NoTranslate as="div" data-testid="schedule-summary-end-date" className="text-gray-darker">
                    {endDate}
                </NoTranslate>
            </FormGroup>
        </div>
    );
};

export const CustomDates = ({ dates }: { dates?: string[] }) => {
    return (
        <FormGroup>
            <Label>Dates</Label>
            <div className="flex flex-wrap gap-2">
                {dates?.map((date) => (
                    <Badge key={date} color="secondary">
                        <NoTranslate>{date}</NoTranslate>
                    </Badge>
                ))}
            </div>
        </FormGroup>
    );
};
